import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'

import clsx from 'clsx'
import 'react-sortable-tree/style.css'

// @material-ui
import {
  Box,
  Icon,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@material-ui/core'

// core
import { useGlobal } from '_core/hooks/useGlobal'
import { withLang } from '_core/hocs/withLang'
import { withCatch } from '_core/hocs/withCatch'
import { useCartContext } from '_core/contexts/Cart';
import { UserAvatar } from '_core/components/UserAvatar'
import { ClipboardInput } from '_core/components/ClipboardInput'
import { State } from '_core/components/State'
import { Price } from '_core/components/Price'
import { Loading } from '_core/components/Loading'
import { renderProp } from '_core/views/StructureInfo';

// style
import { useStructureInfoStyle } from './structureInfoStyle'


export const STRUCTURE_CONTACT_FIELDS = [
  ['mobile', 'smartphone'],
  ['phone', 'phone'],
  ['email', 'email'],
]


const RawStructureInfo = (props) => {
  const { visitorId, isInline, langInfo, getLangString: l } = props
  
  const classes = useStructureInfoStyle()
  
  const [data, setData] = useState({})
  const [nodeKey, setNodeKey] = useState(0)
  const [visitorInfo, setVisitorInfo] = useState({})
  
  const cart = useCartContext();

  const {
    request: { queue },
    config: { API_METHOD },
  } = useGlobal()

  useEffect(() => {
    setNodeKey(nk => nk + 1)
  }, [langInfo, visitorId, cart.currency])

  const getVisitorInfo = async () => {
    if (data[visitorId] != null) {
      setVisitorInfo(data[visitorId])
      return
    }

    const result = await queue(API_METHOD.VISITOR_STRUCTURE_INFO, {
      id: visitorId,
      place: 'struct',
      currency: cart.currency.id,
    })

    setData(data => ({ ...data, [visitorId]: result }))
    setVisitorInfo(result)
  }

  const renderContact = (value, icon) =>
    value != null && value.trim().length > 0 ? (
      <ListItem disableGutters="true" className={classes.contact}>
        <ListItemIcon className={classes.contact}>
          <Icon className={classes.contactIcon} fontSize="small">
            {icon}
          </Icon>
        </ListItemIcon>

        <ListItemText
          primary={value}
          primaryTypographyProps={{ noWrap: true }}
        />
      </ListItem>
    ) : null

  const renderField = (field, data) => {
    if (data === Object(data)) {
      if (data.hasOwnProperty('title')) {
        return (
          <ClipboardInput
            label={l(`r._.structure.info.${field}`)}
            value={data.title}
            helperText=""
          />
        )
      } else if (data.hasOwnProperty('login')) {
        return (
          <ClipboardInput
            label={l(`r._.structure.info.${field}`)}
            value={data.login}
            helperText=""
          />
        )
      }

      if (data.hasOwnProperty('value')) {
        return (
          <Price
            value={data.value}
            currency={cart.currency.id}
            label={l(`r._.structure.info.${field}`)}
          />
        )
      }

      return null
    }

    return (
      <ClipboardInput
        label={l(`r._.structure.info.${field}`)}
        value={data}
        helperText=""
      />
    )
  }

  const renderPropField = (prop) => {
    if (prop.type === 'header') {
      return (
        <div className={classes.propHeaderField}>
          {renderProp(l, prop)}
        </div>
      );
    } else if (prop.type === 'link') {
      return (
        <div className={classes.propLinkField}>
          {renderProp(l, prop)}
        </div>
      );
    } else {
      return (
        <ClipboardInput
          label={prop.title}
          value={renderProp(l, prop)}
          helperText=""
        />
      );
    }
  };

  const hasConacts = data =>
    STRUCTURE_CONTACT_FIELDS.some(([field]) => data[field] != null)

  const rootClasses = clsx({
    [classes.root]: true,
    [classes.rootInline]: isInline,
  })

  return (
    <div className={rootClasses}>
      {visitorId == null ? (
        <Loading />
      ) : (
        <State
          key={nodeKey}
          waitProps={{ visitorInfo }}
          sideProps={{ getVisitorInfo }}
          render={({ visitorInfo }) => (
            <>
              <Box display="flex" alignItems="center">
                <UserAvatar disableClick userInfo={visitorInfo} size="lg" />

                <Box
                  display="flex"
                  flexDirection="column"
                  className={classes.user}
                >
                  <Typography className={classes.login} noWrap={true}>
                    {visitorInfo.login}
                  </Typography>

                  {visitorInfo.fullname != null ? (
                    <Typography variant="caption" noWrap={true}>
                      {visitorInfo.fullname}
                    </Typography>
                  ) : null}
                </Box>
              </Box>

              {hasConacts(visitorInfo) ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  className={classes.fields}
                >
                  <List disablePadding={true}>
                    {STRUCTURE_CONTACT_FIELDS.map(([field, icon]) =>
                      renderContact(visitorInfo[field], icon)
                    )}
                  </List>
                </Box>
              ) : null}

              {visitorInfo.info ? (
                <div className={classes.fields}>
                  {Object.keys(visitorInfo.info).map((field) =>
                    renderField(field, visitorInfo.info[field])
                  )}
                </div>
              ) : null}

              {visitorInfo.props?.length ? (
                <div className={classes.fields}>
                  {visitorInfo.props.map(renderPropField)}
                </div>
              ) : null}
            </>
          )}
        />
      )}
    </div>
  )
};

RawStructureInfo.defaultProps = {}

RawStructureInfo.propTypes = {
  // self props
  visitorId: PropTypes.number,
  isInline: PropTypes.bool,

  // `withLang` HOC props
  langInfo: PropTypes.object,
  langStrings: PropTypes.object,
  getLangObject: PropTypes.func,
  getFirstLangString: PropTypes.func,
  getLangStringSet: PropTypes.func,
  getLangString: PropTypes.func.isRequired,
}

export const StructureInfo = withCatch(withLang(RawStructureInfo))
